import { CopyInput } from "@/components";
import { ScrollableLayoutContainer } from "@/components/layout-right-drawer";
import { PreviewToolbarContainer } from "@/components/module-api-editor";
import {
  EditorInputProps,
  WorkspaceRole,
} from "@/components/module-api-editor/types";
import { VisualPreviewOpenAPI } from "@/components/module-api-editor/visual-preview-openapi";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useExportOpenAPI } from "@/hooks/use-export-openapi";
import {
  buildLiveOpenApiFileUrl,
  DEFAULT_ICON_SIZE,
  NormIcons,
} from "@/lib/utils";
import { Link } from "@tanstack/react-router";
import { ReactNode } from "react";

export function EditorPreviewCreateClient({
  organizationSlug,
  workspaceSlug,
  isAuthed,
  onChange,
  value,
  workspaceRole,
  draftId,
  extraToolbarItems,
}: EditorInputProps & {
  organizationSlug?: string;
  workspaceSlug?: string;
  isAuthed: boolean;
  workspaceRole: WorkspaceRole;
  draftId?: string;
  extraToolbarItems: ReactNode;
}) {
  const handleExportOpenAPI = useExportOpenAPI({ value });

  const hasManagedOpenAPIFile = organizationSlug && workspaceSlug;
  return (
    <Tabs defaultValue="client" className="h-full flex flex-col items-stretch">
      <PreviewToolbarContainer
        value={value}
        onChange={onChange}
        isAuthed={isAuthed}
        workspaceRole={workspaceRole}
        workspaceSlug={workspaceSlug}
        organizationSlug={organizationSlug}
        extraToolbarItems={extraToolbarItems}
        tabs={
          <TabsList>
            <TabsTrigger value="client">Client & Contract</TabsTrigger>
            <TabsTrigger value="swagger-ui">Swagger UI</TabsTrigger>
          </TabsList>
        }
      >
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem className="hidden md:block">
              <Link search={(prev) => ({ ...prev, activeElement: undefined })}>
                <BreadcrumbLink asChild>
                  <span>Workspace</span>
                </BreadcrumbLink>
              </Link>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="hidden md:block" />
            <BreadcrumbItem>
              <BreadcrumbPage>Operation</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </PreviewToolbarContainer>
      <ScrollableLayoutContainer>
        <TabsContent value="client" className="grow overflow-auto px-4">
          {hasManagedOpenAPIFile ? (
            <Card className="mx-4">
              <div className="flex items-start justify-between">
                <CardHeader>
                  <CardTitle>Integreate with other apps</CardTitle>
                  <CardDescription>
                    This workspace exposes a live OpenAPI yaml file. Use this
                    file to generate API clients, SDKs, or documentation with
                    3rd party tools.
                  </CardDescription>
                </CardHeader>
                <Button
                  size="sm"
                  className="mt-4 mr-4"
                  onClick={handleExportOpenAPI}
                >
                  <NormIcons.Download
                    size={DEFAULT_ICON_SIZE}
                    className="mr-2"
                  />
                  Download OpenAPI file
                </Button>
              </div>
              <CardContent>
                <div className="flex items-center space-x-2">
                  <div className="grid flex-1 gap-2">
                    <Label htmlFor="link" className="sr-only">
                      Link to OpenAPI spec file
                    </Label>
                    <CopyInput
                      value={buildLiveOpenApiFileUrl({
                        workspaceSlug,
                        organizationSlug,
                        draftId,
                      })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div>
              <Card className="mb-norm">
                <CardHeader>
                  <CardTitle>Download OpenAPI File</CardTitle>
                  <CardDescription>
                    Get a local version of your contract.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    size="sm"
                    className=" mr-4"
                    onClick={handleExportOpenAPI}
                  >
                    <NormIcons.Download
                      size={DEFAULT_ICON_SIZE}
                      className="mr-2"
                    />
                    Download OpenAPI file
                  </Button>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>
                    Powerful integrations & auto-generated API clients
                  </CardTitle>
                  <CardDescription>
                    We rely on third party tools to generate powerful
                    integrations like API Documentation, API Clients, SDKs, and
                    more. Use integrations directly form the API contract
                    defined in this workspace.
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  <Link to="/login">
                    <Button>Sign up</Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
        <TabsContent
          value="swagger-ui"
          className="dark:bg-white bg-white mt-0 grow overflow-auto"
        >
          <VisualPreviewOpenAPI value={value} onChange={onChange} />
        </TabsContent>
      </ScrollableLayoutContainer>
    </Tabs>
  );
}
